import { BulkItemParam, CpaNavigationParams, Frequency, ServiceCode } from '@shared/ui-service';

// Converts "YYYY-MM-DD" → "YYYY-MM-DDT00:00:00" so JS treats it as local time, not UTC.
// Datetime strings that already have a time component are returned unchanged.
const normalizeDate = (raw: string | null): string | undefined => {
  if (!raw) return undefined;
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00:00` : raw;
};

export function parseCpaQueryParamsFromUrl(url: string | URL): CpaNavigationParams | null {
  const parsedUrl = typeof url === 'string' ? new URL(url) : url;
  const searchParams = parsedUrl.searchParams;

  const requiredKeys: (keyof CpaNavigationParams)[] = [
    'city',
    'state',
    'zip',
    'street',
    'code',
    'latitude',
    'longitude',
  ];

  for (const key of requiredKeys) {
    const value = searchParams.get(key);
    if (!value) {
      console.warn(`Missing required query param: ${key}`);
      return null;
    }
  }
  const containerIdRaw = searchParams.get('containerId');
  // Commercial params
  const trashContainerSizeRaw = searchParams.get('trashContainerSize');
  const trashFrequencyRaw = searchParams.get('trashFrequency');
  const trashQuantityRaw = searchParams.get('trashQuantity');
  const recyclingContainerSizeRaw = searchParams.get('recyclingContainerSize');
  const recyclingFrequencyRaw = searchParams.get('recyclingFrequency');
  const recyclingQuantityRaw = searchParams.get('recyclingQuantity');
  const foodContainerSizeRaw = searchParams.get('foodContainerSize');
  const foodFrequencyRaw = searchParams.get('foodFrequency');
  const foodQuantityRaw = searchParams.get('foodQuantity');
  const startDateRaw = searchParams.get('startDate');
  // Commercial Bulk params
  const bulkItemsRaw = searchParams.get('bulkItems');
  const pickupDateRaw = searchParams.get('pickupDate');
  // Roll-Off (Dumpster) params
  const deliveryDateRaw = searchParams.get('deliveryDate');
  const dumpsterQuantityRaw = searchParams.get('dumpsterQuantity');
  const containerLocationRaw = searchParams.get('containerLocation');
  // Compactor params
  const compactorMaterialsRaw = searchParams.get('compactorMaterials');
  const compactorServiceDateRaw = searchParams.get('compactorServiceDate');

  // Parse bulkItems encoded pairs (format: "itemId1:qty1,itemId2:qty2,itemId3")
  const parseBulkItems = (raw: string | null): BulkItemParam[] | undefined => {
    if (!raw) return undefined;
    const items: BulkItemParam[] = [];
    const pairs = raw.split(',');
    for (const pair of pairs) {
      const [itemId, quantityStr] = pair.split(':');
      if (itemId) {
        items.push({
          itemId: itemId.trim(),
          quantity: quantityStr ? parseInt(quantityStr.trim(), 10) : undefined,
        });
      }
    }
    return items.length > 0 ? items : undefined;
  };

  const result: CpaNavigationParams = {
    city: searchParams.get('city') as string,
    state: searchParams.get('state') as string,
    zip: searchParams.get('zip') as string,
    street: searchParams.get('street') as string,
    latitude: parseFloat(searchParams.get('latitude') as string),
    longitude: parseFloat(searchParams.get('longitude') as string),
    code: searchParams.get('code') as ServiceCode,
    customerType: (searchParams.get('customerType') as CpaNavigationParams['customerType']) || undefined,
    containerType: (searchParams.get('containerType') as CpaNavigationParams['containerType']) || undefined,
    containerId: containerIdRaw ? parseInt(containerIdRaw, 10) : undefined,
    // Commercial Trash params
    trashContainerSize: trashContainerSizeRaw ? parseInt(trashContainerSizeRaw, 10) : undefined,
    trashFrequency: trashFrequencyRaw ? (trashFrequencyRaw as Frequency) : undefined,
    trashQuantity: trashQuantityRaw ? parseInt(trashQuantityRaw, 10) : undefined,
    // Commercial Recycling params
    recyclingContainerSize: recyclingContainerSizeRaw ? parseInt(recyclingContainerSizeRaw, 10) : undefined,
    recyclingFrequency: recyclingFrequencyRaw ? (recyclingFrequencyRaw as Frequency) : undefined,
    recyclingQuantity: recyclingQuantityRaw ? parseInt(recyclingQuantityRaw, 10) : undefined,
    // Commercial Food params
    foodContainerSize: foodContainerSizeRaw ? parseInt(foodContainerSizeRaw, 10) : undefined,
    foodFrequency: foodFrequencyRaw ? (foodFrequencyRaw as Frequency) : undefined,
    foodQuantity: foodQuantityRaw ? parseInt(foodQuantityRaw, 10) : undefined,
    // Shared Commercial param
    startDate: normalizeDate(startDateRaw),
    // Commercial Bulk params
    bulkItems: parseBulkItems(bulkItemsRaw),
    pickupDate: normalizeDate(pickupDateRaw),
    // Roll-Off (Dumpster) params
    deliveryDate: normalizeDate(deliveryDateRaw),
    dumpsterQuantity: dumpsterQuantityRaw ? parseInt(dumpsterQuantityRaw, 10) : undefined,
    containerLocation: containerLocationRaw || undefined,
    // Compactor params
    compactorMaterials: compactorMaterialsRaw || undefined,
    compactorServiceDate: normalizeDate(compactorServiceDateRaw),
  };

  return result;
}
